import { createContext, FC, ReactNode, useContext, useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { CHAT_TYPES } from "@constants/chat";

import { useAppDispatch, useAppSelector } from "@app/hooks";

import { clearJobFields, setJobFields } from "@features/jobManagement/slice";

import { Conversation } from "@components/AssistantChat/type";
import { useConversations, useMessages } from "@components/Conversation/hooks";

import { Priority, QuestVisibility } from "@pages/jobs-management/constants";
import { PostJobFormType } from "@pages/post-job/types";

interface IToasterContextProps {
  children: ReactNode;
}

type PostJobFormData = Partial<PostJobFormType>;

interface Draft {
  text: string;
  imageUrls: { name: string; url: string }[];
  fileUrls: { name: string; url: string }[];
}

interface UseGawainAssistant {
  currentSession: string;
  setCurrentSession: (sessionId: string) => void;
  draft: Draft;
  setDraft: (draft: Draft) => void;
  gawainTicketRes: PostJobFormType;
  setGawainTicketRes: (data: PostJobFormType) => void;
  conversations: Conversation[];
  loadingConversations: boolean;
  loadingMessages: boolean;
  messages: any[];
  updatePostJobForm: (
    fields: PostJobFormType,
    useForm: UseFormReturn<PostJobFormType, any, undefined>,
    formId?: string
  ) => void;
  postJobFormData: PostJobFormData;
  setPostJobFormData: (data: PostJobFormData) => void;
  activeFormId: string | null;
  setActiveFormId: (formId: string | null) => void;
}

const initialJobFormData: PostJobFormData = {
  title: "",
  description: "",
  detailOfWork: "",
  budget: 0,
  dueDate: null,
  priority: null,
  visibility: null,
  files: [],
  skills: [],
};

export const GawainAssistantContext = createContext(null);

export const useGawainAssistant = () =>
  useContext<UseGawainAssistant>(GawainAssistantContext);

const GawainAssistantProvider: FC<IToasterContextProps> = ({ children }) => {
  const appSelector = useAppSelector((state) => state.auth.userInfo);

  const [currentSession, setCurrentSession] = useState<string>("");
  const [draft, setDraft] = useState<Draft>(null);
  const [gawainTicketRes, setGawainTicketRes] = useState<any>(null);
  const [postJobFormData, setPostJobFormData] =
    useState<PostJobFormData>(initialJobFormData);
  const [activeFormId, setActiveFormId] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  const [conversations, loadingConversations] = useConversations(
    appSelector?.user?.id,
    CHAT_TYPES.ASSISTANT
  );
  const [messages, loadingMessages] = useMessages(currentSession);

  useEffect(() => {
    return () => {
      setPostJobFormData(initialJobFormData);
      setGawainTicketRes("");
      // setCurrentSession("");
      setActiveFormId(null);
      dispatch(clearJobFields());
    };
  }, [activeFormId]);

  const updatePostJobForm = (
    fields: PostJobFormType,
    useForm: UseFormReturn<PostJobFormType, any, undefined>,
    formId?: string
  ) => {
    if (!fields || !useForm) {
      return;
    }

    if (formId && formId !== activeFormId) {
      return;
    }

    const { setValue } = useForm;
    Object.entries(fields).forEach((key) => {
      if (
        key[1] == null ||
        key[1] == "" ||
        (Array.isArray(key[1]) && key[1].length == 0)
      ) {
        return;
      }

      switch (key[0]) {
        case "skills":
          setValue(
            key[0] as keyof PostJobFormType,
            (key[1] as unknown as string[]).map((skill: string) => {
              return { id: "", name: skill, text: skill, label: skill };
            })
          );
          break;
        case "overview":
          setValue("description", key[1] as string);
          break;
        case "ticket":
          setValue("detailOfWork", key[1] as string);
          break;
        default:
          setValue(key[0] as keyof PostJobFormType, key[1]);
      }
    });

    // Reset the form data
    setPostJobFormData(initialJobFormData);
  };

  return (
    <GawainAssistantContext.Provider
      value={{
        currentSession,
        setCurrentSession,
        draft,
        setDraft,
        gawainTicketRes,
        setGawainTicketRes,
        conversations,
        loadingConversations,
        messages,
        loadingMessages,
        updatePostJobForm,
        postJobFormData,
        setPostJobFormData,
        activeFormId,
        setActiveFormId,
      }}
    >
      {children}
    </GawainAssistantContext.Provider>
  );
};

export default GawainAssistantProvider;
